"use client";

import { useQuery } from "@tanstack/react-query";
import { defaultTo, get } from "lodash-es";
import { type ComponentProps, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslations, useLocale } from "use-intl";

import { AdminOfferDiscoveryButton } from "@/components/admin-offer-discovery-button";
import { AlbumCoverWithMedia } from "@/components/album-cover-with-media";
import { ArrowForwardIcon, NoteIcon, SearchIcon } from "@/components/appIcons";
import { ArtistLinks } from "@/components/artist-links";
import { formatBarcodeForDisplay } from "@/components/barcode-list";
import { BioText } from "@/components/bio-text";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CatalogCollapsibleContent } from "@/components/catalog-collapsible-content";
import { CatalogCreditsCompaniesFaq } from "@/components/catalog-credits-companies-faq";
import { CatalogCreditsCompaniesSection } from "@/components/catalog-credits-companies-section";
import { CatalogFaqAccordion } from "@/components/catalog-faq-accordion";
import { CatalogPricingFaq } from "@/components/catalog-pricing-faq";
import { CountryFlag } from "@/components/country-flag";
import { FeaturePageBacklinks } from "@/components/feature-page-backlinks/feature-page-backlinks";
import { GenreStyleTags } from "@/components/genre-style-tags";
import { Link } from "@/components/link";
import { CatalogListActions } from "@/components/lists/CatalogListActions";
import { CollectionStateChip } from "@/components/lists/collection-state-chip";
import { CatalogDealOffers } from "@/components/marketplace/CatalogDealOffers";
import { getDealOfferSummaryQueryOptions } from "@/components/marketplace/deal-offers-query";
import { ReleaseMarketplaceSection } from "@/components/marketplace/listings-section";
import { PriceStats } from "@/components/marketplace/price-stats";
import { ReleaseOfferOverview } from "@/components/marketplace/release-offer-overview";
import { AlertsCallout } from "@/components/messaging/alerts-callout";
import { DetailActions } from "@/components/music/detail-actions";
import { DetailBackLink } from "@/components/music/detail-back-link";
import { type DetailFact, DetailFactsRow } from "@/components/music/detail-facts-row";
import { DetailHeroLayout } from "@/components/music/detail-hero-layout";
import { DetailPageShell } from "@/components/music/detail-page-shell";
import { LastfmScrobbleButton } from "@/components/music/lastfm-scrobble-button";
import { MusicTypeChip } from "@/components/music/music-type-chip";
import { SourceLinks } from "@/components/music/source-links";
import { getSpotifyAlbumQueryOptions } from "@/components/music/spotify-album-query";
import { PageJumpNav, type PageJumpNavItem } from "@/components/page-jump-nav";
import { PlayAllButton } from "@/components/play-all-button";
import { ReleaseFormatFeatureTags } from "@/components/release-format-feature-tags";
import { ReleaseImageGallery } from "@/components/release-image-gallery";
import { RichTextHtml } from "@/components/rich-text-html";
import { ShareButton } from "@/components/share-button";
import { SpotifyPlaybackButton } from "@/components/SpotifyPlaybackButton";
import { WikipediaSection } from "@/components/wikipedia-section";
import { useCurrency } from "@/hooks/use-currency";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { useFormatDate } from "@/hooks/use-format-date";
import { useFormatNumber } from "@/hooks/use-format-number";
import { useRecordView } from "@/hooks/use-record-view";
import { useReleaseContentController } from "@/hooks/use-release-content-controller";
import { useReleaseImagesResource } from "@/hooks/use-release-images-resource";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import { formatDuration } from "@/lib/format-duration";
import {
  buildReleaseFormatDisplay,
  type ReleaseFormatDetailSection,
  type ReleaseFormatDisplay,
} from "@/lib/release-format-display";
import { extractReleaseSeoYear } from "@/lib/seo";
import { orpc } from "@/utils/orpc.client";
import { formatCountry } from "@garimpa/lib/countries";
import { getPrimaryFormatCategory } from "@garimpa/lib/formatClassification";
import type { ReleaseImprint } from "@garimpa/lib/musicTypes";

import { ReleaseTracklist } from "./release-tracklist";

const FORMAT_DETAIL_SEARCH_THRESHOLD = 10;
const LABEL_CATALOG_SEARCH_THRESHOLD = 10;
const BARCODE_SCROLL_THRESHOLD = 8;
const HIDDEN_TYPE_SIGNALS = new Set(["album", "single_ep", "compilation", "other"]);
const POLICY_SIGNAL_DISPLAY_KEYS: Record<string, string> = {
  promotion: "promo",
  unofficial: "non_official_status",
};
const SECTION_IDS = {
  tracks: "release-tracks",
  prices: "release-prices",
  credits: "release-credits",
  edition: "release-edition",
  faq: "release-faq",
} as const;

type ReleaseContentController = ReturnType<typeof useReleaseContentController>;
type ReleaseContentRelease = ReleaseContentController["release"];
type ReleaseImages = ReturnType<typeof useReleaseImagesResource>["images"];

function ReleaseBreadcrumbs({
  formatDisplay,
  release,
}: {
  formatDisplay: ReleaseFormatDisplay;
  release: ReleaseContentRelease;
}) {
  const t = useTranslations("release");
  const tc = useTranslations("globals.common");

  return (
    <Breadcrumbs className="mb-8 hidden md:flex" label={tc("breadcrumbNavigation")}>
      {release.primaryIndex ? (
        <Link to={release.primaryIndex.path} className="text-muted-foreground">
          {release.primaryIndex.label}
        </Link>
      ) : null}
      {release.artists.length > 0 && (
        <span className="text-muted-foreground">
          <ArtistLinks
            appearance="text"
            artists={release.artists}
            className="text-muted-foreground"
          />
        </span>
      )}
      {release.workId ? (
        <Link appearance="text" to={`/work/${release.workId}`} className="text-muted-foreground">
          {release.workTitle}
        </Link>
      ) : (
        <span className="text-muted-foreground">{release.title}</span>
      )}
      <span className="font-medium text-primary">
        {defaultTo(formatDisplay.summaryText, t("notFound"))}
      </span>
    </Breadcrumbs>
  );
}

function ReleaseHeroMedia({
  id,
  release,
  releaseImages,
}: {
  id: string;
  release: ReleaseContentRelease;
  releaseImages: ReleaseImages;
}) {
  const artistName = release.artists
    .map((artist) => `${artist.name}${defaultTo(artist.joinSeparator, "")}`)
    .join("");

  return (
    <div className="lg:col-span-5 relative flex flex-col items-center lg:sticky lg:top-20 lg:self-start min-w-0 overflow-hidden lg:overflow-visible">
      <AlbumCoverWithMedia
        format={defaultTo(get(release.mediaFormats, "0"), null)}
        imageUrl={release.imageUrl}
        title={release.title}
        artistName={artistName}
      >
        <AlbumCoverWithMedia.Media />
        <AlbumCoverWithMedia.Cover />
      </AlbumCoverWithMedia>
      <ReleaseImageGallery images={releaseImages} releaseId={id} title={release.title} />
    </div>
  );
}

interface ReleaseIdentityProps {
  formatDisplay: ReleaseFormatDisplay;
  id: string;
  isInCollection: ReleaseContentController["isInCollection"];
  release: ReleaseContentRelease;
  releaseSignalTags: string[];
  showWorkTypeChip: boolean;
}

/** Artist → title → edition line (format summary) with the release's signal chips. */
function ReleaseIdentity({
  formatDisplay,
  id,
  isInCollection,
  release,
  releaseSignalTags,
  showWorkTypeChip,
}: ReleaseIdentityProps) {
  const t = useTranslations("release");
  const tcol = useTranslations("shared.collection");
  const status = defaultTo(release.status, "");
  const showStatus = [Boolean(status), status !== "accepted"].every(Boolean);
  const editionLine = formatDisplay.summaryItems.map((item) => item.text).join(" + ");

  return (
    <div className="space-y-3">
      {release.artists.length > 0 && (
        <p className="text-lg font-bold md:text-xl">
          <ArtistLinks appearance="text" artists={release.artists} className="text-primary" />
        </p>
      )}
      <h1 className="text-4xl font-black leading-none tracking-tighter hyphens-auto [overflow-wrap:anywhere] md:text-5xl lg:text-6xl">
        {release.title}
      </h1>
      <div className="flex flex-wrap items-center gap-2">
        {editionLine && <span className="mr-1 text-lg font-semibold">{editionLine}</span>}
        {showStatus && (
          <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-bold text-amber-500">
            {t.has(status) ? t(status) : status}
          </span>
        )}
        <ReleaseFormatFeatureTags
          mediaFeatures={releaseSignalTags}
          maxVisible={3}
          className="contents"
          tagClassName="rounded-full px-2.5"
        />
        {showWorkTypeChip && <MusicTypeChip type={release.workType} />}
        <CollectionStateChip
          inCollection={isInCollection({ releaseId: id })}
          collectionLabel={tcol("inCollection")}
        />
      </div>
    </div>
  );
}

interface ReleaseFactsInput {
  durationSeconds: number | null | undefined;
  formatDate: ReturnType<typeof useFormatDate>;
  formatNumber: (value: number) => string;
  hasDuration: boolean;
  hasTrackCount: boolean;
  heroCatalogNumber: string | null;
  locale: string;
  release: ReleaseContentRelease;
  t: ReturnType<typeof useTranslations<"release">>;
}

function releaseTrackSummary(input: ReleaseFactsInput): string | null {
  const parts = [
    input.hasTrackCount ? input.formatNumber(input.release.trackCount) : null,
    input.hasDuration ? formatDuration(defaultTo(input.durationSeconds, 0)) : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Only facts that exist; the row wraps, so long values never break a fixed grid. */
function buildReleaseFacts(input: ReleaseFactsInput): DetailFact[] {
  const { release, t } = input;
  const trackSummary = releaseTrackSummary(input);
  const facts: Array<DetailFact | null> = [
    release.date
      ? {
          key: "date",
          label: t("releaseDate"),
          value: <time dateTime={release.date}>{input.formatDate(release.date)}</time>,
        }
      : null,
    release.originalYear
      ? { key: "originalYear", label: t("originalYear"), value: release.originalYear }
      : null,
    release.country
      ? {
          key: "country",
          label: t("country"),
          value: (
            // Server (Bun) and browser ICU data can name some countries differently.
            <span className="inline-flex items-center gap-1.5" suppressHydrationWarning>
              <CountryFlag code={release.country} decorative />
              {formatCountry(release.country, input.locale)}
            </span>
          ),
        }
      : null,
    input.heroCatalogNumber
      ? {
          key: "catalog",
          label: t("catalogNumber"),
          value: <span className="font-mono text-[15px]">{input.heroCatalogNumber}</span>,
        }
      : null,
    trackSummary ? { key: "tracks", label: t("tracks"), value: trackSummary } : null,
  ];
  return facts.filter((fact): fact is DetailFact => fact !== null);
}

function ReleaseNotes({
  notesHtml,
  notesText,
}: {
  notesHtml: string | null;
  notesText: string | null;
}) {
  const t = useTranslations("release");

  if (![notesHtml, notesText].some(Boolean)) return null;

  return (
    <section aria-labelledby="release-notes-title">
      <div className="flex items-center gap-1.5 mb-2">
        <NoteIcon className="text-sm text-muted-foreground" decorative />
        <h3
          id="release-notes-title"
          className="text-xs text-muted-foreground font-medium uppercase tracking-wider"
        >
          {t("notes")}
        </h3>
      </div>
      {notesHtml ? (
        <RichTextHtml
          safeHtml={notesHtml}
          maxLines={6}
          className="text-sm text-foreground/90 leading-relaxed"
        />
      ) : (
        <BioText
          text={defaultTo(notesText, "")}
          maxLines={6}
          className="text-sm text-foreground/90 leading-relaxed"
        />
      )}
    </section>
  );
}

interface ReleaseActionsProps {
  handlePlayAll: ReleaseContentController["handlePlayAll"];
  hasPlayableTracks: boolean;
  id: string;
  isPlayAllResolving: boolean;
  isSpotifyResolving: boolean;
  release: ReleaseContentRelease;
  spotifyUrl: string | undefined;
}

function ReleaseActions({
  handlePlayAll,
  hasPlayableTracks,
  id,
  isPlayAllResolving,
  isSpotifyResolving,
  release,
  spotifyUrl,
}: ReleaseActionsProps) {
  const t = useTranslations("release");
  const ta = useTranslations("shared.detailActions");

  return (
    <DetailActions
      listening={
        <PlayAllButton
          canPlayAll={hasPlayableTracks}
          isPlayAllResolving={isPlayAllResolving}
          onClick={handlePlayAll}
          label={t("listenRelease")}
          unavailableLabel={ta("listeningUnavailable")}
          scrollTargetId={SECTION_IDS.tracks}
        />
      }
      listeningProvider={
        <SpotifyPlaybackButton
          isResolving={isSpotifyResolving}
          label={t("openOnSpotify")}
          url={spotifyUrl}
          variant="compact"
          workId={defaultTo(release.workId, undefined)}
        />
      }
      collection={
        release.workId ? (
          <CatalogListActions
            target={{ entityType: "release", workId: release.workId, releaseId: id }}
          />
        ) : undefined
      }
      overflow={
        <>
          <LastfmScrobbleButton
            entity="release"
            id={id}
            variant="pill"
            className="min-h-11 w-full justify-start rounded-lg border-transparent bg-transparent px-3 hover:bg-[#D51007]/10"
          />
          <ShareButton
            title={release.title}
            className="min-h-11 w-full justify-start px-3 py-2.5"
          />
        </>
      }
      admin={<AdminOfferDiscoveryButton catalogId={id} entityType="release" />}
    />
  );
}

interface ReleaseEditionDetailsProps {
  additionalCatalogNumbers: string[];
  formatDisplay: ReleaseFormatDisplay;
  formatNumber: (value: number) => string;
  hasReleaseFacts: boolean;
  notesHtml: string | null;
  notesText: string | null;
  release: ReleaseContentRelease;
}

/** Side card: identifier ledger (label/catalog, codes, format), notes and sources. */
function ReleaseEditionDetails({
  additionalCatalogNumbers,
  formatDisplay,
  formatNumber,
  hasReleaseFacts,
  notesHtml,
  notesText,
  release,
}: ReleaseEditionDetailsProps) {
  const t = useTranslations("release");
  const tp = useTranslations("shared.pageJumpNav");
  const tb = useTranslations("shared.bioText");
  const [expanded, setExpanded] = useState(false);
  const hasNotes = [notesHtml, notesText].some(Boolean);
  const hasSources = (release.sourceLinks?.length ?? 0) > 0;

  if (![hasReleaseFacts, hasNotes, hasSources].some(Boolean)) return null;

  return (
    <section
      id={SECTION_IDS.edition}
      aria-labelledby="release-edition-title"
      className="scroll-mt-36 space-y-4 rounded-2xl border border-border/70 bg-card/40 p-4 sm:p-5"
    >
      <h2 id="release-edition-title" className="text-lg font-bold tracking-tight">
        {tp("editionDetails")}
      </h2>
      {hasReleaseFacts && (
        <CatalogCollapsibleContent
          collapseOnMobile
          contentId="release-identifier-ledger"
          expanded={expanded}
          onExpandedChange={setExpanded}
          showLessLabel={tb("showLess")}
          showMoreLabel={tb("showMore")}
        >
          <ReleaseFactsLedger
            labels={release.imprints}
            catalogNumbers={additionalCatalogNumbers}
            barcodes={release.barcodes}
            asins={release.asins}
            labelCodes={release.labelCodes}
            distributionCodes={release.distributionCodes}
            formatDisplay={formatDisplay}
            hasDetailedFormats={release.mediaFormatDetails.length > 0}
            mediaFeatures={release.mediaFeatures}
            formatText={release.formatText}
            labelLabel={t("labelLedger")}
            catalogNumberLabel={t("catalogNumber")}
            barcodeLabel={t("barcode")}
            asinLabel={t("asin")}
            labelCodeLabel={t("labelCode")}
            distributionCodeLabel={t("distributionCode")}
            formatDetailsLabel={t("formatLedger")}
            filterPlaceholder={`${t("labelLedger")} / ${t("catalogNumber")}`}
            formatNumber={formatNumber}
          />
        </CatalogCollapsibleContent>
      )}
      <ReleaseNotes notesHtml={notesHtml} notesText={notesText} />
      <SourceLinks links={release.sourceLinks} />
    </section>
  );
}

function ReleaseOtherEditionsLink({ release }: { release: ReleaseContentRelease }) {
  const tp = useTranslations("shared.pageJumpNav");

  if (!release.workId) return null;

  return (
    <Link
      to={`/work/${release.workId}`}
      className="group flex items-center gap-3 rounded-2xl border border-primary/35 p-4 transition-colors hover:bg-primary/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-bold text-foreground">
          {tp("otherEditionsOf", { title: defaultTo(release.workTitle, release.title) })}
        </span>
        <span className="text-sm text-muted-foreground">{tp("otherEditionsHint")}</span>
      </span>
      <ArrowForwardIcon
        className="shrink-0 text-lg text-primary transition-transform group-hover:translate-x-0.5"
        decorative
      />
    </Link>
  );
}

function ReleasePrices({ id }: { id: string }) {
  const td = useTranslations("shared.affiliates");

  return (
    <section
      id={SECTION_IDS.prices}
      aria-labelledby="release-prices-title"
      className="scroll-mt-36 space-y-4"
    >
      <h2 id="release-prices-title" className="text-2xl font-bold">
        {td("pricesAndHistory")}
      </h2>
      <ReleaseOfferOverview releaseId={id} />
      <PriceStats releaseId={id} />
      <ReleaseMarketplaceSection releaseId={id} />
    </section>
  );
}

type DealOfferSnapshot = ComponentProps<typeof CatalogDealOffers>["offerSummarySnapshot"];
type DealOfferFormat = ComponentProps<typeof CatalogDealOffers>["format"];

interface ReleaseDealOffersProps {
  dealOfferFormat: DealOfferFormat;
  dealOfferSummarySnapshot: DealOfferSnapshot;
  id: string;
  workId: string | null;
}

function ReleaseDealOffers({
  dealOfferFormat,
  dealOfferSummarySnapshot,
  id,
  workId,
}: ReleaseDealOffersProps) {
  return (
    <CatalogDealOffers
      presentationScope="release"
      releaseId={workId ? undefined : id}
      workId={defaultTo(workId, undefined)}
      contextReleaseId={id}
      offerSummarySnapshot={dealOfferSummarySnapshot}
      format={workId ? dealOfferFormat : undefined}
      target={workId ? { entityType: "release", workId, releaseId: id } : undefined}
    />
  );
}

function getReleaseCatalogNumberSummary(release: ReleaseContentRelease) {
  const labelCatalogNumberKeys = new Set(
    release.imprints
      .map((imprint) => imprint.catno)
      .filter((catno): catno is string => Boolean(catno))
      .map(normalizeCatalogNumberKey),
  );
  const primaryCatalogNumberKey = release.primaryCatno
    ? normalizeCatalogNumberKey(release.primaryCatno)
    : null;
  const additionalCatalogNumbers = release.catalogNumbers.filter((catalogNumber) => {
    const key = normalizeCatalogNumberKey(catalogNumber);
    return [key !== primaryCatalogNumberKey, !labelCatalogNumberKeys.has(key)].every(Boolean);
  });
  const heroCatalogNumber = defaultTo(
    release.primaryCatno,
    additionalCatalogNumbers.length > 0 ? additionalCatalogNumbers.join(" / ") : null,
  );

  return { additionalCatalogNumbers, heroCatalogNumber };
}

function getReleaseDisplayState(
  release: ReleaseContentRelease,
  additionalCatalogNumbers: string[],
) {
  const durationSeconds = release.durationSeconds;
  const hasSecondaryMetadata = [
    additionalCatalogNumbers.length,
    release.barcodes.length,
    release.asins.length,
    release.labelCodes.length,
    release.distributionCodes.length,
  ].some((length) => length > 0);
  const hasTrackCount = release.trackCount > 0;
  const hasDuration = [
    typeof durationSeconds === "number",
    defaultTo(durationSeconds, 0) > 0,
  ].every(Boolean);
  const hasFormatMetadata = [
    release.mediaFormatDetails.length > 0,
    release.mediaFeatures.length > 0,
    Boolean(release.formatText),
  ].some(Boolean);
  const hasReleaseFacts = [
    release.imprints.length > 0,
    hasSecondaryMetadata,
    hasFormatMetadata,
  ].some(Boolean);
  const showWorkTypeChip = [release.workType != null, release.workType !== "other"].every(Boolean);

  return {
    durationSeconds,
    hasDuration,
    hasReleaseFacts,
    hasTrackCount,
    showWorkTypeChip,
  };
}

function hasCreditsOrCompanies(release: ReleaseContentRelease): boolean {
  const hasCredits = Object.values(release.creditsByRole ?? {}).some(
    (credits) => credits.length > 0,
  );
  return hasCredits || (release.companies?.length ?? 0) > 0;
}

function buildReleaseJumpItems(input: {
  hasEditionDetails: boolean;
  hasFaq: boolean;
  hasTracks: boolean;
  release: ReleaseContentRelease;
  tp: ReturnType<typeof useTranslations<"shared.pageJumpNav">>;
}): PageJumpNavItem[] {
  const { release, tp } = input;
  const items: Array<PageJumpNavItem | null> = [
    input.hasTracks
      ? { id: SECTION_IDS.tracks, label: tp("tracks"), count: release.trackCount }
      : null,
    { id: SECTION_IDS.prices, label: tp("prices") },
    hasCreditsOrCompanies(release) ? { id: SECTION_IDS.credits, label: tp("credits") } : null,
    input.hasEditionDetails ? { id: SECTION_IDS.edition, label: tp("editionDetails") } : null,
    input.hasFaq ? { id: SECTION_IDS.faq, label: tp("questions") } : null,
  ];
  return items.filter((item): item is PageJumpNavItem => item !== null);
}

export function ReleaseContent({ id }: { id: string }) {
  useRecordView("release", id);
  const t = useTranslations("release");
  const tp = useTranslations("shared.pageJumpNav");
  const tf = useTranslations("shared.formats");
  const tFeature = useTranslations("shared.mediaFeatures");
  const locale = useLocale();
  const formatNumber = useFormatNumber();
  const formatDate = useFormatDate();
  const { currency } = useCurrency();
  const { viewerId } = useViewerQueryState();
  const {
    release,
    releaseMedia,
    trackVideos,
    isInCollection,
    hasPlayableTracks,
    isPlayAllResolving,
    handlePlayAll,
  } = useReleaseContentController({ id });
  const { images: releaseImages } = useReleaseImagesResource(id);
  const { additionalCatalogNumbers, heroCatalogNumber } = getReleaseCatalogNumberSummary(release);
  const { durationSeconds, hasDuration, hasReleaseFacts, hasTrackCount, showWorkTypeChip } =
    getReleaseDisplayState(release, additionalCatalogNumbers);
  const workId = defaultTo(release.workId, "");
  const hasWork = Boolean(release.workId);
  const computeSpotifyAlbumResource = useCallback(
    () => orpc.music.computeSpotifyAlbumResource.call({ workId }),
    [workId],
  );
  const {
    query: spotifyAlbumQuery,
    resource: spotifyAlbumResource,
    value: spotifyAlbum,
  } = useDeferredResourceQuery({
    queryOptions: getSpotifyAlbumQueryOptions({
      workId,
      enabled: hasWork,
    }),
    compute: computeSpotifyAlbumResource,
  });

  const spotifyUrl = defaultTo(get(spotifyAlbum, "url"), undefined);
  const isSpotifyResolving = [
    hasWork,
    [spotifyAlbumQuery.isLoading, get(spotifyAlbumResource, "status") === "pending"].some(Boolean),
  ].every(Boolean);
  const formatDisplay = buildReleaseFormatDisplay(release.mediaFormatDetails, {
    formatMessages: tf,
    featureMessages: tFeature,
  });
  const dealOfferFormat = defaultTo(
    getPrimaryFormatCategory({
      mediaCategories: release.mediaCategories,
      mediaFormats: release.mediaFormats,
    }),
    "all",
  );
  const dealOfferSummaryQuery = useQuery({
    ...getDealOfferSummaryQueryOptions(
      {
        workId,
        releaseId: id,
        currency,
        format: dealOfferFormat,
      },
      { viewerId },
    ),
    enabled: hasWork,
  });
  const dealOfferSummarySnapshot = hasWork
    ? {
        isLoading: dealOfferSummaryQuery.isLoading,
        summary: defaultTo(get(dealOfferSummaryQuery.data, "0"), null),
      }
    : undefined;
  const releaseSignalTags = buildReleaseSignalTags({
    editionSignals: release.editionSignals,
    packageSignals: release.packageSignals,
    releasePolicySignals: release.releasePolicySignals,
    typeSignals: release.typeSignals,
    hasNonOfficialStatus: release.hasNonOfficialStatus,
    burnedOnly: release.burnedOnly,
    singleMediaFormat: release.singleMediaFormat,
    workType: release.workType,
  });
  const notesHtml = defaultTo(release.notesHtml, null);
  const notesText = defaultTo(release.notes, null);
  const facts = buildReleaseFacts({
    durationSeconds,
    formatDate,
    formatNumber,
    hasDuration,
    hasTrackCount,
    heroCatalogNumber,
    locale,
    release,
    t,
  });
  const hasTracks = releaseMedia.some((medium) => medium.tracks.length > 0);
  const hasEditionDetails = [
    hasReleaseFacts,
    Boolean(notesHtml),
    Boolean(notesText),
    (release.sourceLinks?.length ?? 0) > 0,
  ].some(Boolean);
  const hasFaq =
    hasCreditsOrCompanies(release) || Boolean(get(dealOfferSummarySnapshot, "summary"));
  const jumpItems = buildReleaseJumpItems({ hasEditionDetails, hasFaq, hasTracks, release, tp });

  return (
    <DetailPageShell>
      <ReleaseBreadcrumbs formatDisplay={formatDisplay} release={release} />
      {release.workId && (
        <DetailBackLink
          to={`/work/${release.workId}`}
          label={tp("allEditionsOf", { title: defaultTo(release.workTitle, release.title) })}
        />
      )}
      <DetailHeroLayout
        media={<ReleaseHeroMedia id={id} release={release} releaseImages={releaseImages} />}
        content={
          <div
            id="release-summary"
            className="scroll-mt-20 lg:col-span-7 flex min-w-0 flex-col gap-6"
          >
            <ReleaseIdentity
              formatDisplay={formatDisplay}
              id={id}
              isInCollection={isInCollection}
              release={release}
              releaseSignalTags={releaseSignalTags}
              showWorkTypeChip={showWorkTypeChip}
            />
            <DetailFactsRow facts={facts} />
            <ReleaseActions
              handlePlayAll={handlePlayAll}
              hasPlayableTracks={hasPlayableTracks}
              id={id}
              isPlayAllResolving={isPlayAllResolving}
              isSpotifyResolving={isSpotifyResolving}
              release={release}
              spotifyUrl={spotifyUrl}
            />
            <ReleaseDealOffers
              dealOfferFormat={dealOfferFormat}
              dealOfferSummarySnapshot={dealOfferSummarySnapshot}
              id={id}
              workId={release.workId}
            />
          </div>
        }
      />

      <PageJumpNav
        items={jumpItems}
        trailing={
          release.workId ? (
            <Link
              to={`/work/${release.workId}`}
              title={tp("allEditionsOf", { title: defaultTo(release.workTitle, release.title) })}
              className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <span className="truncate">
                {tp("allEditionsOf", { title: defaultTo(release.workTitle, release.title) })}
              </span>
              <ArrowForwardIcon className="shrink-0" decorative />
            </Link>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 gap-12 pt-10 lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-16">
        <div className="min-w-0 space-y-14">
          {hasTracks && (
            <div id={SECTION_IDS.tracks} className="scroll-mt-36">
              <ReleaseTracklist
                media={releaseMedia}
                artistName={release.primaryArtistName}
                trackVideos={trackVideos}
              />
            </div>
          )}
          <ReleasePrices id={id} />
        </div>
        <aside className="min-w-0 space-y-6" aria-label={tp("editionDetails")}>
          <ReleaseEditionDetails
            additionalCatalogNumbers={additionalCatalogNumbers}
            formatDisplay={formatDisplay}
            formatNumber={formatNumber}
            hasReleaseFacts={hasReleaseFacts}
            notesHtml={notesHtml}
            notesText={notesText}
            release={release}
          />
          <ReleaseOtherEditionsLink release={release} />
          <GenreStyleTags
            genres={release.genres}
            styles={release.styles}
            pages={release.taxonomyPages}
            className="mb-0 gap-2"
            tagClassName="rounded-full bg-secondary px-3 py-1.5"
          />
          <FeaturePageBacklinks
            entityType="release"
            catalogId={id}
            workId={defaultTo(release.workId, undefined)}
          />
          <WikipediaSection wikidataId={release.wikidataId} />
        </aside>
      </div>

      <div id={SECTION_IDS.credits} className="scroll-mt-36 pt-16">
        <CatalogCreditsCompaniesSection
          companies={release.companies}
          creditsByRole={release.creditsByRole}
        />
      </div>

      <section
        id={SECTION_IDS.faq}
        aria-labelledby="catalog-faq-title"
        className="scroll-mt-36 pt-16"
      >
        <CatalogFaqAccordion titleId="catalog-faq-title">
          <CatalogPricingFaq
            artist={release.artistDisplay}
            entityType="release"
            releaseId={id}
            summary={get(dealOfferSummarySnapshot, "summary")}
            title={release.title}
            workId={defaultTo(release.workId, id)}
          />
          <CatalogCreditsCompaniesFaq
            artist={release.artistDisplay}
            companies={release.companies}
            companyScope="release"
            creditsByRole={release.creditsByRole}
            releaseCountry={release.country ? formatCountry(release.country, locale) : null}
            releaseYear={extractReleaseSeoYear(release.date)}
            title={release.title}
          />
        </CatalogFaqAccordion>
      </section>

      <AlertsCallout surface="release_detail" />
    </DetailPageShell>
  );
}

function normalizeCatalogNumberKey(value: string): string {
  return value.toUpperCase().replace(/[.\-\s]/g, "") || value.toUpperCase();
}

function baseReleaseSignalTags(input: {
  hasNonOfficialStatus: boolean;
  burnedOnly: boolean;
  isSingleVideoMediaFormat: boolean;
}): string[] {
  return [
    input.hasNonOfficialStatus ? "non_official_status" : null,
    input.burnedOnly ? "burned_only" : null,
    input.isSingleVideoMediaFormat ? "single_video_media_format" : null,
  ].filter((tag): tag is string => Boolean(tag));
}

function displayReleasePolicySignal(signal: string): string | null {
  if (
    [
      "explicit_official_status",
      "possibly_official_status",
      "non_official_status",
      "burned_only",
    ].includes(signal)
  ) {
    return null;
  }
  return POLICY_SIGNAL_DISPLAY_KEYS[signal] ?? signal;
}

function displayReleaseTypeSignal(
  signal: string,
  isSingleVideoMediaFormat: boolean,
  workType: string | null,
): string | null {
  if (isSingleVideoMediaFormat && signal === "video") return null;
  if (HIDDEN_TYPE_SIGNALS.has(signal) || signal === workType) return null;
  return signal;
}

function buildReleaseSignalTags({
  editionSignals,
  packageSignals,
  releasePolicySignals,
  typeSignals,
  hasNonOfficialStatus,
  burnedOnly,
  singleMediaFormat,
  workType,
}: {
  editionSignals: string[];
  packageSignals: string[];
  releasePolicySignals: string[];
  typeSignals: string[];
  hasNonOfficialStatus: boolean;
  burnedOnly: boolean;
  singleMediaFormat: string | null;
  workType: string | null;
}) {
  const isSingleVideoMediaFormat = singleMediaFormat === "video";
  const tags = [
    ...baseReleaseSignalTags({ hasNonOfficialStatus, burnedOnly, isSingleVideoMediaFormat }),
    ...releasePolicySignals.map(displayReleasePolicySignal),
    ...typeSignals.map((signal) =>
      displayReleaseTypeSignal(signal, isSingleVideoMediaFormat, workType),
    ),
    ...editionSignals,
    ...packageSignals,
  ];

  return [
    ...new Set(
      tags
        .filter((tag): tag is string => Boolean(tag))
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ];
}

interface ReleaseFactsLedgerProps {
  labels: ReleaseImprint[];
  catalogNumbers: string[];
  barcodes: string[];
  asins: string[];
  labelCodes: string[];
  distributionCodes: string[];
  formatDisplay: ReleaseFormatDisplay;
  hasDetailedFormats: boolean;
  mediaFeatures: string[];
  formatText: string | null;
  labelLabel: string;
  catalogNumberLabel: string;
  barcodeLabel: string;
  asinLabel: string;
  labelCodeLabel: string;
  distributionCodeLabel: string;
  formatDetailsLabel: string;
  filterPlaceholder: string;
  formatNumber: (value: number) => string;
}

function ReleaseIdentifierLedgerRows(
  props: Pick<
    ReleaseFactsLedgerProps,
    | "labels"
    | "catalogNumbers"
    | "barcodes"
    | "asins"
    | "labelCodes"
    | "distributionCodes"
    | "labelLabel"
    | "catalogNumberLabel"
    | "barcodeLabel"
    | "asinLabel"
    | "labelCodeLabel"
    | "distributionCodeLabel"
    | "filterPlaceholder"
    | "formatNumber"
  >,
) {
  return (
    <>
      {props.labels.length > 0 && (
        <LedgerRow
          label={props.labelLabel}
          count={props.labels.length}
          formatNumber={props.formatNumber}
        >
          <LabelCatalogLedgerValue
            labels={props.labels}
            catalogNumberLabel={props.catalogNumberLabel}
            filterPlaceholder={props.filterPlaceholder}
          />
        </LedgerRow>
      )}
      {props.catalogNumbers.length > 0 && (
        <LedgerRow
          label={props.catalogNumberLabel}
          count={props.catalogNumbers.length}
          formatNumber={props.formatNumber}
        >
          <MonoValueList values={props.catalogNumbers} />
        </LedgerRow>
      )}
      {props.barcodes.length > 0 && (
        <LedgerRow
          label={props.barcodeLabel}
          count={props.barcodes.length}
          formatNumber={props.formatNumber}
        >
          <BarcodeLedgerValue barcodes={props.barcodes} />
        </LedgerRow>
      )}
      {props.asins.length > 0 && (
        <LedgerRow
          label={props.asinLabel}
          count={props.asins.length}
          formatNumber={props.formatNumber}
        >
          <MonoValueList values={props.asins} />
        </LedgerRow>
      )}
      {props.labelCodes.length > 0 && (
        <LedgerRow
          label={props.labelCodeLabel}
          count={props.labelCodes.length}
          formatNumber={props.formatNumber}
        >
          <MonoValueList values={props.labelCodes} />
        </LedgerRow>
      )}
      {props.distributionCodes.length > 0 && (
        <LedgerRow
          label={props.distributionCodeLabel}
          count={props.distributionCodes.length}
          formatNumber={props.formatNumber}
        >
          <MonoValueList values={props.distributionCodes} />
        </LedgerRow>
      )}
    </>
  );
}

function ReleaseFormatLedgerRows({
  formatBodySections,
  formatDetailCount,
  formatDetailsLabel,
  formatNumber,
  formatText,
  hasDetailedFormats,
  hasFallbackFormat,
  mediaFeatures,
}: Pick<
  ReleaseFactsLedgerProps,
  "formatDetailsLabel" | "formatNumber" | "formatText" | "hasDetailedFormats" | "mediaFeatures"
> & {
  formatBodySections: ReturnType<typeof buildFormatDetailBodySections>;
  formatDetailCount: number;
  hasFallbackFormat: boolean;
}) {
  return (
    <>
      {hasDetailedFormats && formatDetailCount > 0 && (
        <LedgerRow label={formatDetailsLabel} count={formatDetailCount} formatNumber={formatNumber}>
          <FormatLedgerValue
            sections={formatBodySections}
            formatDetailsLabel={formatDetailsLabel}
          />
        </LedgerRow>
      )}
      {hasFallbackFormat && (
        <LedgerRow label={formatDetailsLabel} formatNumber={formatNumber}>
          <div className="flex flex-wrap items-center gap-1.5">
            {mediaFeatures.length > 0 && <ReleaseFormatFeatureTags mediaFeatures={mediaFeatures} />}
            {formatText && (
              <span className="inline-flex max-w-full items-center whitespace-normal break-normal rounded border border-border/60 bg-background/55 px-2 py-1 text-right text-xs font-medium text-foreground">
                {formatText}
              </span>
            )}
          </div>
        </LedgerRow>
      )}
    </>
  );
}

function ReleaseFactsLedger({
  labels,
  catalogNumbers,
  barcodes,
  asins,
  labelCodes,
  distributionCodes,
  formatDisplay,
  hasDetailedFormats,
  mediaFeatures,
  formatText,
  labelLabel,
  catalogNumberLabel,
  barcodeLabel,
  asinLabel,
  labelCodeLabel,
  distributionCodeLabel,
  formatDetailsLabel,
  filterPlaceholder,
  formatNumber,
}: ReleaseFactsLedgerProps) {
  const hasFallbackFormat =
    !hasDetailedFormats && (mediaFeatures.length > 0 || Boolean(formatText));
  const formatBodySections = buildFormatDetailBodySections(formatDisplay.detailSections);
  const formatDetailCount = getFormatDetailBodyRowCount(formatBodySections);

  return (
    <div className="border-y border-border/50">
      <dl className="divide-y divide-border/50">
        <ReleaseIdentifierLedgerRows
          labels={labels}
          catalogNumbers={catalogNumbers}
          barcodes={barcodes}
          asins={asins}
          labelCodes={labelCodes}
          distributionCodes={distributionCodes}
          labelLabel={labelLabel}
          catalogNumberLabel={catalogNumberLabel}
          barcodeLabel={barcodeLabel}
          asinLabel={asinLabel}
          labelCodeLabel={labelCodeLabel}
          distributionCodeLabel={distributionCodeLabel}
          filterPlaceholder={filterPlaceholder}
          formatNumber={formatNumber}
        />
        <ReleaseFormatLedgerRows
          formatBodySections={formatBodySections}
          formatDetailCount={formatDetailCount}
          formatDetailsLabel={formatDetailsLabel}
          formatNumber={formatNumber}
          formatText={formatText}
          hasDetailedFormats={hasDetailedFormats}
          hasFallbackFormat={hasFallbackFormat}
          mediaFeatures={mediaFeatures}
        />
      </dl>
    </div>
  );
}

function LedgerRow({
  label,
  count,
  formatNumber,
  children,
}: {
  label: string;
  count?: number;
  formatNumber: (value: number) => string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 items-start gap-1.5 py-2.5">
      <dt className="flex min-w-0 items-center gap-2">
        <span className="whitespace-nowrap text-[11px] font-semibold uppercase leading-5 tracking-wider text-muted-foreground">
          {label}
          {count != null ? <span className="sr-only"> ({formatNumber(count)})</span> : null}
        </span>
      </dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function LabelCatalogLedgerValue({
  labels,
  catalogNumberLabel,
  filterPlaceholder,
}: {
  labels: ReleaseImprint[];
  catalogNumberLabel: string;
  filterPlaceholder: string;
}) {
  const [query, setQuery] = useState("");
  const hasSearch = labels.length > LABEL_CATALOG_SEARCH_THRESHOLD;
  const normalizedQuery = normalizeLabelCatalogQuery(query);
  const visibleLabels = useMemo(() => {
    if (!hasSearch || normalizedQuery.length === 0) return labels;
    return labels.filter((label) =>
      normalizeLabelCatalogQuery(`${label.imprintName} ${label.catno ?? ""}`).includes(
        normalizedQuery,
      ),
    );
  }, [hasSearch, labels, normalizedQuery]);

  if (labels.length <= 2) {
    return (
      <div className="space-y-1">
        {labels.map((label, index) => (
          <CompactLabelCatalogPair
            key={`${label.imprintName}-${label.companyId ?? "unknown"}-${label.catno ?? "no-catno"}-${index}`}
            label={label}
            compact
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {hasSearch && (
        <label className="relative block min-w-0">
          <SearchIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[16px] text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={filterPlaceholder}
            aria-label={filterPlaceholder}
            className="h-11 w-full rounded-md border border-border/70 bg-background/70 pl-8 sm:h-8 pr-2 text-sm font-medium text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary/60 focus:bg-background"
          />
        </label>
      )}
      <div className={hasSearch ? "max-h-80 overflow-y-auto pr-1" : ""}>
        <div className="space-y-1.5">
          {visibleLabels.length > 0
            ? visibleLabels.map((label, index) => (
                <CompactLabelCatalogPair
                  key={`${label.imprintName}-${label.companyId ?? "unknown"}-${label.catno ?? "no-catno"}-${index}`}
                  label={label}
                  catalogNumberLabel={catalogNumberLabel}
                />
              ))
            : null}
        </div>
      </div>
    </div>
  );
}

function CompactLabelCatalogPair({
  label,
  catalogNumberLabel,
  compact = false,
}: {
  label: ReleaseImprint;
  catalogNumberLabel?: string;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <div className="min-w-0 text-sm font-bold leading-6">
          {label.companyId ? (
            <Link
              appearance="text"
              to={`/company/${label.companyId}`}
              className="break-words text-foreground"
            >
              {label.imprintName}
            </Link>
          ) : (
            <span className="break-words">{label.imprintName}</span>
          )}
        </div>
        {label.catno ? (
          <span
            className="rounded-md border border-border/60 bg-background/55 px-2 py-1 font-mono text-xs font-semibold tabular-nums text-foreground"
            title={catalogNumberLabel}
          >
            {label.catno}
          </span>
        ) : (
          <span className="font-mono text-xs text-muted-foreground/60" aria-hidden="true">
            -
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1 rounded-md bg-background/35 px-2.5 py-1.5 text-left">
      <div className="min-w-0 text-sm font-bold">
        {label.companyId ? (
          <Link
            appearance="text"
            to={`/company/${label.companyId}`}
            className="break-words text-foreground"
          >
            {label.imprintName}
          </Link>
        ) : (
          <span className="break-words">{label.imprintName}</span>
        )}
      </div>
      {label.catno ? (
        <span
          className="rounded-md border border-border/60 bg-background/55 px-2 py-1 font-mono text-xs font-semibold tabular-nums text-foreground"
          title={catalogNumberLabel}
        >
          {label.catno}
        </span>
      ) : (
        <span className="font-mono text-xs text-muted-foreground/60" aria-hidden="true">
          -
        </span>
      )}
    </div>
  );
}

function BarcodeLedgerValue({ barcodes }: { barcodes: string[] }) {
  const hasScroll = barcodes.length > BARCODE_SCROLL_THRESHOLD;

  if (barcodes.length <= 2) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {barcodes.map((barcode) => (
          <span
            key={barcode}
            className="rounded-md border border-border/60 bg-background/55 px-2 py-1 font-mono text-xs font-semibold tabular-nums text-foreground"
            title={barcode}
          >
            {formatBarcodeForDisplay(barcode)}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className={hasScroll ? "max-h-36 overflow-y-auto pr-1" : ""}>
      <div className="grid grid-cols-1 gap-x-8 px-2.5">
        {barcodes.map((barcode) => (
          <span
            key={barcode}
            className="border-t border-border/45 py-1.5 text-right font-mono text-xs font-semibold tabular-nums text-foreground first:border-t-0 sm:[&:nth-child(2)]:border-t-0"
            title={barcode}
          >
            {formatBarcodeForDisplay(barcode)}
          </span>
        ))}
      </div>
    </div>
  );
}

function MonoValueList({ values }: { values: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((value) => (
        <span
          key={value}
          className="rounded-md border border-border/60 bg-background/55 px-2 py-1 font-mono text-xs font-semibold tabular-nums text-foreground"
        >
          {value}
        </span>
      ))}
    </div>
  );
}

function normalizeLabelCatalogQuery(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[.\-\s]/g, "");
}

function FormatLedgerValue({
  sections,
  formatDetailsLabel,
}: {
  sections: ReleaseFormatDetailBodySection[];
  formatDetailsLabel: string;
}) {
  const [query, setQuery] = useState("");
  const totalBodyRowCount = getFormatDetailBodyRowCount(sections);
  const hasSearch = totalBodyRowCount > FORMAT_DETAIL_SEARCH_THRESHOLD;
  const normalizedQuery = normalizeLabelCatalogQuery(query);
  const shouldRightAlignSingleRow = !hasSearch && totalBodyRowCount === 1;
  const visibleSections = useMemo(() => {
    if (!hasSearch || normalizedQuery.length === 0) return sections;

    return sections
      .map((section) => ({
        ...section,
        rows: section.rows.filter((row) =>
          normalizeLabelCatalogQuery(
            `${row.seq} ${row.extraText} ${row.label} ${row.text ?? ""} ${row.featureLabels.join(" ")}`,
          ).includes(normalizedQuery),
        ),
      }))
      .filter((section) => section.rows.length > 0);
  }, [sections, hasSearch, normalizedQuery]);
  const hasBody = visibleSections.length > 0;

  return (
    <div className="space-y-2 text-left">
      {hasSearch && (
        <label className="relative block min-w-0">
          <SearchIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[16px] text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={formatDetailsLabel}
            aria-label={formatDetailsLabel}
            className="h-11 w-full rounded-md border border-border/70 bg-background/70 pl-8 sm:h-8 pr-2 text-sm font-medium text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary/60 focus:bg-background"
          />
        </label>
      )}
      <div className={hasSearch ? "max-h-80 overflow-y-auto pr-1" : ""}>
        <div className="space-y-1.5">
          {hasBody
            ? visibleSections.flatMap((section) =>
                section.rows.map((row) => (
                  <div
                    key={row.key}
                    className={
                      shouldRightAlignSingleRow
                        ? "min-w-0 text-left text-sm font-medium leading-5 text-foreground/90"
                        : "min-w-0 rounded-md bg-background/35 px-2.5 py-1.5 text-left text-sm font-medium leading-5 text-foreground/90"
                    }
                  >
                    <span className={shouldRightAlignSingleRow ? "break-normal" : "break-words"}>
                      {row.extraText}
                    </span>
                  </div>
                )),
              )
            : null}
        </div>
      </div>
    </div>
  );
}

type ReleaseFormatDetailBodyRow = ReleaseFormatDetailSection["rows"][number] & {
  extraText: string;
};

type ReleaseFormatDetailBodySection = Omit<ReleaseFormatDetailSection, "rows"> & {
  rows: ReleaseFormatDetailBodyRow[];
};

function buildFormatDetailBodySections(
  sections: ReleaseFormatDetailSection[],
): ReleaseFormatDetailBodySection[] {
  return sections
    .map((section) => ({
      ...section,
      rows: section.rows.filter(hasExtraText),
    }))
    .filter((section) => section.rows.length > 0);
}

function hasExtraText(
  row: ReleaseFormatDetailSection["rows"][number],
): row is ReleaseFormatDetailBodyRow {
  return typeof row.extraText === "string" && row.extraText.length > 0;
}

function getFormatDetailBodyRowCount(sections: ReleaseFormatDetailBodySection[]): number {
  return sections.reduce((count, section) => count + section.rows.length, 0);
}
